import React from "react"

import Typography from "../../../../components/shared/Typography"

/**
 * Color Mode values:
 * - "white": Pure white background (#FFFFFF)
 * - "light": Theme's light background color (bgLight)
 * - "dark": Theme's primary color as background with light text
 */
export const COLOR_MODES = {
  WHITE: "white",
  LIGHT: "light",
  DARK: "dark"
}

const BackgroundColorSelector = ({
  formData,
  onFormDataChange,
  onSettingsChange,
  onColorModeChange,
  disabled = false,
  sectionType,
  showHeaderContent = true,
  theme
}) => {
  // Sections that should not show color mode selector
  // - Hero/Sub-hero with full-image layouts are handled separately in their editors
  // - Quote and Banner sections don't get this option
  const excludedSections = ["quote", "cta_banner"]

  // If this section should not have color mode selector, don't show it
  if (excludedSections.includes(sectionType)) {
    return null
  }

  // Get current color mode from settings, defaulting to "white" for backward compatibility
  const getCurrentColorMode = () => {
    const settings = formData?.settings || {}

    // New format: colorMode is explicitly set
    if (settings.colorMode) {
      return settings.colorMode
    }

    // Legacy format: convert from showBackgroundColor/backgroundColor
    if (settings.showBackgroundColor === true || settings.showBackgroundColor === "true") {
      if (settings.backgroundColor === "theme") {
        return COLOR_MODES.LIGHT
      }
    }

    return COLOR_MODES.WHITE
  }

  const colorMode = getCurrentColorMode()

  const handleColorModeChange = (newMode) => {
    if (disabled) return

    if (onColorModeChange) {
      onColorModeChange(newMode)
      return
    }

    // Use onSettingsChange if available (for live updates), otherwise fall back to onFormDataChange
    if (onSettingsChange) {
      // Set the new colorMode
      onSettingsChange("colorMode", newMode)

      // Also update legacy fields for backward compatibility
      if (newMode === COLOR_MODES.WHITE) {
        onSettingsChange("showBackgroundColor", false)
        onSettingsChange("backgroundColor", "#ffffff")
      } else if (newMode === COLOR_MODES.LIGHT) {
        onSettingsChange("showBackgroundColor", true)
        onSettingsChange("backgroundColor", "theme")
      } else if (newMode === COLOR_MODES.DARK) {
        onSettingsChange("showBackgroundColor", true)
        onSettingsChange("backgroundColor", "dark")
      }
    } else if (onFormDataChange) {
      const newSettings = {
        ...formData.settings,
        colorMode: newMode,
        // Legacy fields for backward compatibility
        showBackgroundColor: newMode !== COLOR_MODES.WHITE,
        backgroundColor: newMode === COLOR_MODES.WHITE ? "#ffffff" : newMode === COLOR_MODES.LIGHT ? "theme" : "dark"
      }

      onFormDataChange({
        ...formData,
        settings: newSettings
      })
    }
  }

  // Get preview color for the color mode indicator
  const getPreviewColor = (mode) => {
    switch (mode) {
      case COLOR_MODES.WHITE:
        return "#FFFFFF"
      case COLOR_MODES.LIGHT:
        return theme?.colors?.bgLight || "#F4ECE9"
      case COLOR_MODES.DARK:
        return theme?.colors?.primary || "#BD654F"
      default:
        return "#FFFFFF"
    }
  }

  const colorOptions = [
    {
      id: COLOR_MODES.WHITE,
      label: "White",
      description: "Clean white background"
    },
    {
      id: COLOR_MODES.LIGHT,
      label: "Light",
      description: "Theme's light background"
    },
    {
      id: COLOR_MODES.DARK,
      label: "Dark",
      description: "Theme's primary color"
    }
  ]

  return (
    <div className="space-y-3">
      {showHeaderContent && (
        <div className="border-b border-gray-200 pb-4">
          <Typography variant="h5" className="font-bold text-gray-dark">
            Section background color
          </Typography>
          <Typography variant="body" className="mt-1 text-gray-600">
            Choose the color mode for this section&apos;s background.
          </Typography>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <Typography variant="body" className="font-medium text-gray-dark">
          Background color
        </Typography>

        <div className="flex flex-row gap-4">
          {colorOptions.map((option) => (
            <label
              key={option.id}
              className={`
                flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 p-3 transition-all
                ${colorMode === option.id ? "border-teal-500 bg-teal-50" : "border-gray-200 hover:border-gray-300"}
                ${disabled ? "cursor-not-allowed opacity-50" : ""}
              `}
              onClick={() => handleColorModeChange(option.id)}>
              <input
                type="radio"
                name={`colorMode-${sectionType}`}
                value={option.id}
                checked={colorMode === option.id}
                onChange={() => handleColorModeChange(option.id)}
                disabled={disabled}
                className="sr-only"
              />

              {/* Color preview circle */}
              <div
                className="size-8 rounded-full border border-gray-300 shadow-sm"
                style={{ backgroundColor: getPreviewColor(option.id) }}
              />

              <span
                className={`
                text-sm font-medium
                ${colorMode === option.id ? "text-teal-700" : "text-gray-700"}
              `}>
                {option.label}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  )
}

export default BackgroundColorSelector
